import React, {useEffect, useState} from 'react';
import {ProjectJson} from "../../api/json/ProjectJson"
import {CircularProgress, Stack, ToggleButton, ToggleButtonGroup} from "@mui/material"
import {useToasts} from "../../hooks/useToasts"
import {QualityCheckRequests} from "../../api/requests/QualityCheckRequests"
import {CustomModelRequests} from "../../api/requests/CustomModelRequests"
import {ModelPlatform} from "../../api/enum/ModelPlatform"
import {EndpointStatus} from "../../api/enum/EndpointStatus"
import TestRunList from "./TestRunList"
import {AnyCustomModelJson} from "../../api/json/CustomModelJson"
import TestRunScoringPanel from "./TestRunScoringPanel"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import QualityCheckSectionHeader from "./QualityCheckSectionHeader"
import ClassifierMetricsPanel from "./ClassifierMetricsPanel"
import ClassifierMetricsSkeleton from "./ClassifierMetricsSkeleton"
import {ClassifierMetricsJson} from "../../api/json/ClassifierMetricsJson"
import ClassifierTemplateUtil from "../../library/ClassifierTemplateUtil"
import {ClassifierFilter, hasActiveClassifierFilter} from "../../library/ClassifierFilter"

interface EvalSectionProps {
    project: ProjectJson
    model: AnyCustomModelJson
    updateQualityCheck: (qualityCheckJson: QualityCheckJson) => void
}

function QualityCheckSection({project, model, updateQualityCheck}: EvalSectionProps) {
    const [filter, setFilter] = useState<"all" | "unscored">("all")
    const [endpointStarting, setEndpointStarting] = useState<boolean>()
    const [classifierMetrics, setClassifierMetrics] = useState<ClassifierMetricsJson | null>(null)
    const [metricsLoaded, setMetricsLoaded] = useState(false)
    const [classifierFilter, setClassifierFilter] = useState<ClassifierFilter>({})

    const classOptions = ClassifierTemplateUtil.outputClassColumn(model.template, project.columns)?.predefinedOptions

    const updateClassifierFilter = (updated: ClassifierFilter) => {
        setClassifierFilter(updated)
        if (hasActiveClassifierFilter(updated)) setFilter("all")
    }

    const toasts = useToasts()

    const qualityCheckJson = model.qualityCheck

    const loadQualityCheck = () => {
        QualityCheckRequests.get(model.qualityCheck.id)
            .then(updateQualityCheck)
            .catch(toasts.showError)
    }

    const reload = () => {
        if (model.qualityCheck.completed || model.qualityCheck.failed)
            return

        loadQualityCheck()
    }

    useEffect(() => {
        const interval = setInterval(() => reload(), 2000)
        return () => clearInterval(interval)
    }, [model])

    useEffect(() => {
        setMetricsLoaded(false)
        QualityCheckRequests.classifierMetrics(model.qualityCheck.id)
            .then(metrics => {
                setClassifierMetrics(metrics)
                setMetricsLoaded(true)
            })
            .catch(() => setMetricsLoaded(true))
    }, [model.qualityCheck.id, qualityCheckJson.completed])

    useEffect(() => {
        if (model.platform !== ModelPlatform.TOGETHER || model.qualityCheck.completed) {
            setEndpointStarting(false)
            return
        }

        let active = true
        const checkEndpoint = () => {
            CustomModelRequests.endpoints(model.id)
                .then(endpoints => {
                    if (!active) return
                    const current = endpoints.find(endpoint => endpoint.status !== EndpointStatus.DELETED)
                    setEndpointStarting(current?.status === EndpointStatus.STARTING)
                })
                .catch(() => {})
        }
        checkEndpoint()
        const interval = setInterval(checkEndpoint, 2000)
        return () => {
            active = false
            clearInterval(interval)
        }
    }, [model.id, model.platform, model.qualityCheck.completed])

    if (!qualityCheckJson) {
        return <CircularProgress/>
    }

    const testRunsToScore = qualityCheckJson.stats.completedTestRuns - qualityCheckJson.stats.scoredTestRuns
    return (
        <Stack spacing={3}>
            <QualityCheckSectionHeader
                project={project}
                qualityCheckJson={qualityCheckJson}
                totalValidationExamples={project.stats.numberOfValidationRows}
                updateQualityCheck={updateQualityCheck}
                endpointStarting={endpointStarting}
            />

            {qualityCheckJson.completed && !metricsLoaded && (classOptions?.length ?? 0) > 0 &&
                <ClassifierMetricsSkeleton classCount={classOptions!.length}/>
            }
            {qualityCheckJson.completed && metricsLoaded && classifierMetrics && classifierMetrics.rows.some(row => row.total > 0) &&
                <ClassifierMetricsPanel metrics={classifierMetrics}
                                        classOptions={classOptions}
                                        filter={classifierFilter}
                                        onFilterChange={updateClassifierFilter}/>
            }

            {testRunsToScore > 0 &&
                <ToggleButtonGroup
                    value={filter}
                    size="medium"
                    exclusive
                    onChange={(event, value) => {
                        if (!value) return
                        event.currentTarget.scrollIntoView({behavior: 'auto', block: 'start'});
                        setFilter(value)
                    }}
                    aria-label="evaluations filter"
                    sx={{alignSelf: "flex-start"}}
                >
                    <ToggleButton value="unscored" sx={{pl: 2, pr: 2}}>
                        Review ({testRunsToScore})
                    </ToggleButton>
                    <ToggleButton value="all" sx={{pl: 2, pr: 2}}>
                        All ({qualityCheckJson.stats.completedTestRuns})
                    </ToggleButton>
                </ToggleButtonGroup>
            }

            {qualityCheckJson.stats.completedTestRuns > 0 &&
                (filter === "unscored" && testRunsToScore > 0
                    ? <TestRunScoringPanel
                        model={model}
                        reloadQualityCheckStats={loadQualityCheck}
                    />
                    : <TestRunList
                        model={model}
                        classOptions={classifierMetrics ? classOptions : undefined}
                        classifierFilter={classifierFilter}
                        refreshSignal={qualityCheckJson.stats.completedTestRuns}
                        reloadQualityCheckStats={loadQualityCheck}/>)
            }
        </Stack>
    );
}

export default QualityCheckSection;